import { Injectable } from '@angular/core';
import { Observable, from, switchMap, map } from 'rxjs';
import { AuthService } from './auth.service';
import {
  CertificateStatus,
  QualificationStatus,
  ScreeningDocType,
  ScreeningUploadFiles,
  TutorScreening,
} from '../microservice-clients/tutor-screening.model';

@Injectable({ providedIn: 'root' })
export class TutorScreeningService {

  constructor(private authService: AuthService) { }

  submitScreeningDocuments(files: Map<ScreeningDocType, File>): Observable<TutorScreening> {
    const encode = Promise.all(
      Array.from(files.entries()).map(([docType, file]) =>
        this.fileToDataUri(file).then(dataUri => ({ docType, dataUri })),
      ),
    );

    return from(encode).pipe(
      switchMap(encoded => {
        const payload: ScreeningUploadFiles = {};
        for (const { docType, dataUri } of encoded) {
          if (docType === ScreeningDocType.Certificate) {
            payload.certificate = { file: dataUri };
          } else {
            payload.qualification = { file: dataUri };
          }
        }
        const user = { id: this.authService.userDetails.id, tutorDetails: { screening: payload } } as any;
        return this.authService.updateUserDetails(user).pipe(
          map(updated => updated?.tutorDetails?.screening ?? {}),
        );
      }),
    );
  }

  private fileToDataUri(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /** user-service accepts a certificate upload only while the record is missing or rejected. */
  certificateRequiresUpload(screening: TutorScreening | undefined): boolean {
    const cert = screening?.certificate;
    return !cert || cert.status === CertificateStatus.Rejected;
  }

  documentsRequiringUpload(screening: TutorScreening | undefined): ScreeningDocType[] {
    const needed: ScreeningDocType[] = [];
    if (this.certificateRequiresUpload(screening)) {
      needed.push(ScreeningDocType.Certificate);
    }
    const qual = screening?.qualification;
    if (!qual || qual.status === QualificationStatus.Rejected) {
      needed.push(ScreeningDocType.Qualification);
    }

    return needed;
  }

  isScreeningComplete(screening: TutorScreening | undefined): boolean {
    const certOk = screening?.certificate?.status === CertificateStatus.Approved;
    const qualStatus = screening?.qualification?.status;
    const qualOk = qualStatus === QualificationStatus.MinimumMet
      || qualStatus === QualificationStatus.ExceptionGranted;
    return certOk && qualOk;
  }
}
